import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import supabase from './supabaseClient';

const PROFILE_TABLE = 'profiles';

function Dashboard() {
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [todayWorkout, setTodayWorkout] = useState(null);
  const [streak] = useState(3);
  const [level] = useState(1);
  const [workoutProgress] = useState(60);
  const navigate = useNavigate();

  useEffect(() => {
    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      if (session) {
        setUser(session.user);
        fetchDashboardData(session.user.id, session.user.email);
      } else {
        navigate('/');
      }
      setLoading(false);
    });

    // Listen for auth changes
    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        setUser(session.user);
        fetchDashboardData(session.user.id, session.user.email);
      } else {
        navigate('/');
      }
    });

    return () => subscription.unsubscribe();
  }, [navigate]);

  const fetchDashboardData = async (userId, userEmail) => {
    try {
      // Get profile or create if it doesn't exist
      const { data: profileData, error: profileError } = await supabase
        .from(PROFILE_TABLE)
        .select('*')
        .eq('auth_user_id', userId)
        .maybeSingle();

      if (profileError && profileError.code !== 'PGRST116') {
        console.error('Error fetching profile:', profileError);
        return;
      }

      // If profile doesn't exist, create one
      let activeProfile = profileData;

      if (!activeProfile) {
        const { data: newProfile, error: createError } = await supabase
          .from(PROFILE_TABLE)
          .insert({
            auth_user_id: userId,
            email: userEmail,
            display_name: userEmail?.split('@')[0] || 'User',
          })
          .select()
          .single();

        if (createError) {
          console.error('Error creating profile:', createError);
          return;
        }

        activeProfile = newProfile;
      }

      setProfile(activeProfile);
      
      // Get today's scheduled workout (only if we have a profile)
      if (activeProfile?.id) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const todayISO = today.toISOString();
        const tomorrowISO = new Date(today.getTime() + 24 * 60 * 60 * 1000).toISOString();
        
        const { data: scheduled, error: scheduledError } = await supabase
          .from('scheduled_workouts')
          .select(`
            *,
            workouts (
              id,
              title,
              description,
              exercises
            )
          `)
          .eq('owner_id', activeProfile.id)
          .gte('scheduled_for', todayISO)
          .lt('scheduled_for', tomorrowISO)
          .eq('status', 'scheduled')
          .order('scheduled_for', { ascending: true })
          .limit(1)
          .maybeSingle();

        if (scheduledError) {
          console.error('Error fetching scheduled workout:', scheduledError);
        } else {
          setTodayWorkout(scheduled);
        }
      }
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    }
  };

  const getInitials = (name) => {
    if (!name) return 'U';
    const parts = name.split(' ');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const getUserName = () => {
    return profile?.display_name || user?.email?.split('@')[0] || 'User';
  };

  const formatTime = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleTimeString('en-US', { 
      hour: 'numeric', 
      minute: '2-digit',
      hour12: true 
    });
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-xl text-gray-600">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-20">
      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                {profile?.display_name ? getInitials(profile.display_name) : 'U'}
              </div>
              <div>
                <h1 className="text-xl font-semibold text-gray-900">Hi, {getUserName()}!</h1>
                <div className="flex items-center gap-4 text-sm text-gray-600 mt-1">
                  <span>🔥 Streak: {streak} days</span>
                  <span>💪 Level: {level}</span>
                </div>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/exercises')}
                className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors text-sm"
              >
                Exercise Library
              </button>
              <button
                onClick={handleLogout}
                className="text-sm text-gray-600 hover:text-gray-900 border border-gray-300 hover:border-gray-400 px-4 py-2 rounded-lg transition-colors"
              >
                Log out
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        {/* Today's Snapshot */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">TODAY'S SNAPSHOT</h2>
          {todayWorkout ? (
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <span className="text-xl">📅</span>
                <span className="text-gray-700">
                  Today's Workout: <strong>{todayWorkout.workouts?.title || todayWorkout.title || 'Push Day'}</strong> ({todayWorkout.workouts?.description || 'Upper Body'})
                </span>
              </div>
              {todayWorkout.scheduled_for && (
                <div className="flex items-center gap-2">
                  <span className="text-xl">⏰</span>
                  <span className="text-gray-700">
                    Scheduled: <strong>{formatTime(todayWorkout.scheduled_for)}</strong>
                  </span>
                </div>
              )}
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-xl">✅</span>
                  <span className="text-gray-700">Progress:</span>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-full bg-gray-200 rounded-full h-4 flex-1">
                    <div
                      className="bg-blue-500 h-4 rounded-full transition-all"
                      style={{ width: `${workoutProgress}%` }}
                    ></div>
                  </div>
                  <span className="text-sm font-medium text-gray-700">{workoutProgress}% Complete</span>
                </div>
              </div>
              <button className="bg-blue-500 hover:bg-blue-600 text-white px-6 py-2 rounded-lg transition-colors flex items-center gap-2">
                <span>▶️</span>
                Continue Workout
              </button>
            </div>
          ) : (
            <div className="text-gray-500">No workout scheduled for today</div>
          )}
        </div>

        {/* Activity Summary */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">ACTIVITY SUMMARY</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🔥</span>
                <span className="text-sm text-gray-600">Calories Burned</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">412 kcal</div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">👣</span>
                <span className="text-sm text-gray-600">Steps</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">8,346</div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">🕒</span>
                <span className="text-sm text-gray-600">Active Minutes</span>
              </div>
              <div className="text-2xl font-bold text-gray-900">57 min</div>
            </div>
            <div>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-xl">❤️</span>
                <span className="text-sm text-gray-600">Heart Rate</span>
              </div>
              <div className="text-lg font-bold text-gray-900">
                Avg: 122 bpm<br />
                <span className="text-sm font-normal">Max: 168 bpm</span>
              </div>
            </div>
          </div>
        </div>

        {/* Progress Tracker */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">PROGRESS TRACKER</h2>
          <div className="space-y-4">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <span className="text-xl">📈</span>
                <span className="text-gray-700 font-medium">Strength Trend (7-day avg)</span>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Bench</div>
                  <div className="text-xl font-bold text-green-700">+5 lb</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Squat</div>
                  <div className="text-xl font-bold text-green-700">+10 lb</div>
                </div>
                <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                  <div className="text-sm text-gray-600 mb-1">Deadlift</div>
                  <div className="text-xl font-bold text-green-700">+15 lb</div>
                </div>
              </div>
            </div>
            <div className="pt-4 border-t border-gray-200">
              <div className="text-gray-700">
                <strong>Weight:</strong> 172 lb → 170 lb
              </div>
            </div>
            <button className="text-blue-500 hover:text-blue-600 text-sm font-medium">
              View Detailed Stats →
            </button>
          </div>
        </div>

        {/* Insights */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">INSIGHTS</h2>
          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
              <span className="text-xl">🧠</span>
              <p className="text-gray-700">
                "You've improved your training consistency!"
              </p>
            </div>
            <div className="flex items-start gap-3 p-3 bg-yellow-50 rounded-lg">
              <span className="text-xl">💡</span>
              <p className="text-gray-700">
                "Try increasing rest between sets to 90 sec."
              </p>
            </div>
          </div>
        </div>

        {/* Quick Actions */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">QUICK ACTIONS</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <button className="bg-blue-500 hover:bg-blue-600 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
              <span>➕</span>
              <span>Log Set</span>
            </button>
            <button className="bg-green-500 hover:bg-green-600 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
              <span>📅</span>
              <span>Schedule</span>
            </button>
            <button className="bg-orange-500 hover:bg-orange-600 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
              <span>🍽</span>
              <span>Nutrition</span>
            </button>
            <button className="bg-gray-500 hover:bg-gray-600 text-white px-4 py-3 rounded-lg transition-colors flex items-center justify-center gap-2">
              <span>⚙️</span>
              <span>Settings</span>
            </button>
          </div>
        </div>
      </main>

      {/* Footer Navigation Bar */}
      <footer className="fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 shadow-lg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-around items-center py-3">
            <button
              onClick={() => navigate('/dashboard')}
              className="flex flex-col items-center gap-1 text-blue-500 hover:text-blue-600 transition-colors"
            >
              <span className="text-xl">🏠</span>
              <span className="text-xs font-medium">Home</span>
            </button>
            <button
              onClick={() => navigate('/exercises')}
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span className="text-xl">💪</span>
              <span className="text-xs font-medium">Workouts</span>
            </button>
            <button className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors">
              <span className="text-xl">📊</span>
              <span className="text-xs font-medium">Progress</span>
            </button>
            <button
              onClick={() => navigate('/profile')}
              className="flex flex-col items-center gap-1 text-gray-600 hover:text-gray-900 transition-colors"
            >
              <span className="text-xl">⚙️</span>
              <span className="text-xs font-medium">Profile</span>
            </button>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Dashboard;
